
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CalendarIcon, Quote, Edit } from 'lucide-react';
import { Memory } from '@/types/memory';
import ShareMemory from './ShareMemory';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { updateQuote, updateMilestone, getStoredChildren } from '@/utils/storage';
import { logger } from '@/utils/logger';

interface MemoryCardProps {
  memory: Memory;
  onMemoryUpdated?: (updatedMemory: Memory) => void;
}

const MemoryCard = ({ memory, onMemoryUpdated }: MemoryCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editingContent, setEditingContent] = useState(memory.content);
  const [editingContext, setEditingContext] = useState(memory.context || '');
  const [editingChildId, setEditingChildId] = useState(memory.childId);
  const [editingDate, setEditingDate] = useState(memory.date);
  const [children, setChildren] = useState<{ id: string; name: string }[]>([]);

  // Load children data when editing starts
  useEffect(() => {
    if (isEditing) {
      const fetchChildren = async () => {
        try {
          const storedChildren = await getStoredChildren();
          if (storedChildren.length > 0) {
            setChildren(storedChildren.map(child => ({ id: child.id, name: child.name })));
          }
          // Reset the editing data
          setEditingContent(memory.content);
          setEditingContext(memory.context || '');
          setEditingChildId(memory.childId);
          setEditingDate(memory.date);
        } catch (error) {
          logger.error("Error fetching children:", error);
          toast({
            title: "Error",
            description: "Failed to load children data.",
            variant: "destructive"
          });
        }
      };

      fetchChildren();
    }
  }, [isEditing, memory]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleUpdate = async () => {
    if (!editingContent || !editingChildId || !editingDate) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    try {
      if (memory.type === 'quote') {
        const updatedQuote = await updateQuote({
          id: memory.id,
          content: editingContent,
          childId: editingChildId,
          date: editingDate,
          context: editingContext.trim() || undefined,
          childName: children.find(c => c.id === editingChildId)?.name || memory.childName
        });

        if (updatedQuote) {
          setIsEditing(false);
          if (onMemoryUpdated) {
            onMemoryUpdated({
              ...updatedQuote,
              type: 'quote'
            });
          }
        }
      } else {
        const updatedMilestone = await updateMilestone({
          id: memory.id,
          content: editingContent,
          childId: editingChildId,
          date: editingDate,
          context: editingContext.trim() || undefined,
          childName: children.find(c => c.id === editingChildId)?.name || memory.childName
        });

        if (updatedMilestone) {
          setIsEditing(false);
          if (onMemoryUpdated) {
            onMemoryUpdated({
              ...updatedMilestone,
              type: 'milestone'
            });
          }
        }
      }
    } catch (error) {
      logger.error("Error updating memory:", error);
      toast({
        title: "Error",
        description: `Failed to update ${memory.type}. Please try again.`,
        variant: "destructive"
      });
    }
  };

  return (
    <>
      <Card key={memory.id}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            {memory.type === 'quote' ? (
              <Quote className="h-4 w-4 text-primary" />
            ) : (
              <CalendarIcon className="h-4 w-4 text-primary" />
            )}
            <span>{memory.childName}</span>
            <span className="text-gray-500 text-sm ml-auto font-normal">
              {new Date(memory.date).toLocaleDateString()}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="italic text-left">
            {memory.content}
          </p>
          {memory.context && (
            <p className="text-sm text-gray-500 mt-1 text-left">{memory.context}</p>
          )}
        </CardContent>
        <CardFooter className="flex justify-start space-x-2 pt-0">
          <Button variant="outline" size="sm" onClick={handleEditClick}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <ShareMemory memory={memory} />
        </CardFooter>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="sm:max-w-[500px] w-[95%]">
          <DialogHeader>
            <DialogTitle>Edit {memory.type === 'quote' ? 'Quote' : 'Milestone'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-child">Child</Label>
              <Select
                onValueChange={(value) => setEditingChildId(value)}
                value={editingChildId}
              >
                <SelectTrigger className="rounded-md">
                  <SelectValue placeholder="Select a child" />
                </SelectTrigger>
                <SelectContent>
                  {children.map(child => (
                    <SelectItem key={child.id} value={child.id}>{child.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-content">{memory.type === 'quote' ? 'Quote' : 'Milestone'}</Label>
              <Textarea
                id="edit-content"
                placeholder={memory.type === 'quote' ? "What did they say?" : "What milestone was achieved?"}
                value={editingContent}
                onChange={(e) => setEditingContent(e.target.value)}
                className="min-h-[80px] rounded-md max-h-[150px] overflow-y-auto"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-date">Date</Label>
              <Input
                id="edit-date"
                type="date"
                value={editingDate}
                onChange={(e) => setEditingDate(e.target.value)}
                className="rounded-md"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-context">Context (optional)</Label>
              <Textarea
                id="edit-context"
                placeholder={memory.type === 'quote' ? "What was happening when they said this?" : "Any additional details"}
                value={editingContext}
                onChange={(e) => setEditingContext(e.target.value)}
                className="rounded-md max-h-[100px] overflow-y-auto"
              />
            </div>
            <Button onClick={handleUpdate} className="mt-2 rounded-full">
              Update {memory.type === 'quote' ? 'Quote' : 'Milestone'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MemoryCard;
